import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Search, UserCheck, Building2, AlertCircle, Loader2 } from 'lucide-react';
import { impersonationAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const KIND_TABS = [
  { value: 'all', label: 'All' },
  { value: 'agent', label: 'Agents' },
  { value: 'agency', label: 'Agencies' },
];

export const ImpersonationSearchDialog = ({ open, onOpenChange }) => {
  const { startImpersonation } = useAuth();
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState('all');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submittingId, setSubmittingId] = useState(null);

  // Debounced search
  const fetchResults = useCallback(async (q, k) => {
    setLoading(true);
    try {
      const res = await impersonationAPI.search(q, k, 25);
      setResults(res.data.results || []);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Search failed';
      toast.error(typeof msg === 'string' ? msg : 'Search failed');
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => fetchResults(query, kind), 250);
    return () => clearTimeout(t);
  }, [query, kind, open, fetchResults]);

  // Reset state on close
  useEffect(() => {
    if (!open) {
      setQuery('');
      setKind('all');
      setResults([]);
      setSubmittingId(null);
    }
  }, [open]);

  const handleImpersonate = async (item) => {
    if (!item.impersonable) {
      toast.error(item.reason_unavailable || 'Cannot impersonate this user');
      return;
    }
    setSubmittingId(`${item.kind}:${item.target_id}`);
    try {
      await startImpersonation(item.kind, item.target_id);
      toast.success(`Viewing as ${item.label}`);
      onOpenChange(false);
      // Navigate to dashboard so the impersonated user lands on their home
      window.location.href = '/dashboard';
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to start impersonation';
      toast.error(typeof msg === 'string' ? msg : 'Failed to start impersonation');
      setSubmittingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="impersonation-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCheck className="w-5 h-5 text-amber-600" />
            View as Agent or Agency
          </DialogTitle>
          <DialogDescription>
            Securely sign in as another user to see exactly what they see. Every
            action you take while impersonating is logged with your real account.
          </DialogDescription>
        </DialogHeader>

        {/* Search input + kind tabs */}
        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              autoFocus
              placeholder="Search by name, email, NPN, or agency..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9"
              data-testid="impersonation-search-input"
            />
          </div>

          <div className="flex gap-2">
            {KIND_TABS.map((tab) => (
              <Button
                key={tab.value}
                type="button"
                variant={kind === tab.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setKind(tab.value)}
                data-testid={`impersonation-tab-${tab.value}`}
              >
                {tab.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Results */}
        <div className="border rounded-lg max-h-96 overflow-y-auto" data-testid="impersonation-results">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Searching...
            </div>
          ) : results.length === 0 ? (
            <div className="text-center py-12 text-sm text-slate-500">
              {query ? 'No matches found.' : 'Start typing to search agents and agencies.'}
            </div>
          ) : (
            <ul className="divide-y">
              {results.map((item) => {
                const id = `${item.kind}:${item.target_id}`;
                const isSubmitting = submittingId === id;
                const Icon = item.kind === 'agency' ? Building2 : UserCheck;
                return (
                  <li
                    key={id}
                    className={`flex items-center justify-between gap-3 p-3 hover:bg-slate-50 ${
                      !item.impersonable ? 'opacity-60' : ''
                    }`}
                    data-testid={`impersonation-result-${item.kind}-${item.target_id}`}
                  >
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <Icon className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <div className="font-medium truncate flex items-center gap-2">
                          {item.label}
                          <Badge variant="outline" className="text-[10px] uppercase tracking-wide">
                            {item.kind}
                          </Badge>
                        </div>
                        <div className="text-xs text-slate-500 truncate">
                          {item.secondary}
                          {item.email ? ` · ${item.email}` : ''}
                        </div>
                        {!item.impersonable && item.reason_unavailable && (
                          <div className="flex items-center gap-1 text-xs text-amber-700 mt-1">
                            <AlertCircle className="w-3 h-3" />
                            {item.reason_unavailable}
                          </div>
                        )}
                      </div>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      disabled={!item.impersonable || isSubmitting}
                      onClick={() => handleImpersonate(item)}
                      data-testid={`impersonation-view-as-${item.kind}-${item.target_id}`}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-3 h-3 animate-spin mr-1" /> Starting...
                        </>
                      ) : (
                        'View as'
                      )}
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImpersonationSearchDialog;
